import { DashboardCard } from "@dashboard/components/Card";
import { OrderDetailsFragment, OrderStatus } from "@dashboard/graphql";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { FormattedMessage } from "react-intl";

import { isPickupReadyNotified, isPontoRecolhaAddressMissing } from "../../utils/delivery";
import { orderProgressStepperMessages } from "./messages";
import {
  getActiveStepIndex,
  getOrderProgressSteps,
  getPrimaryAction,
  isInvoiceManuallyMarked,
  isReceivedManuallyMarked,
  OrderPrimaryActionHandlers,
} from "./utils";

interface OrderProgressStepperProps extends OrderPrimaryActionHandlers {
  order: OrderDetailsFragment | undefined;
}

const CIRCLE_SIZE = 28;

export const OrderProgressStepper = ({ order, ...handlers }: OrderProgressStepperProps) => {
  if (!order) {
    return null;
  }

  // A cancelled or expired order has no meaningful forward progress, so show a
  // clear terminal state instead of a partially-filled stepper.
  const isCancelled = order.status === OrderStatus.CANCELED;
  const isExpired = order.status === OrderStatus.EXPIRED;

  if (isCancelled || isExpired) {
    return (
      <DashboardCard data-test-id="order-progress-stepper">
        <DashboardCard.Content>
          <Box display="flex" alignItems="center" justifyContent="center" paddingY={2}>
            <Text size={3} fontWeight="medium" color="critical1">
              <FormattedMessage
                {...(isCancelled
                  ? orderProgressStepperMessages.cancelled
                  : orderProgressStepperMessages.expired)}
              />
            </Text>
          </Box>
        </DashboardCard.Content>
      </DashboardCard>
    );
  }

  const steps = getOrderProgressSteps(order);
  const activeIndex = getActiveStepIndex(steps);
  const primaryAction = getPrimaryAction(order, steps, handlers);
  // Only a manually-marked invoice (not one produced by an invoicing app) can be
  // reverted from the stepper.
  const invoiceManuallyMarked = isInvoiceManuallyMarked(order);
  // The invoice step's primary action generates a real invoice; when no
  // invoicing app is installed that fails, so a manual "mark done" fallback is
  // offered beside it.
  const invoiceStepActive = activeIndex !== -1 && steps[activeIndex]?.key === "invoice";
  // "Payment" is the active step for as long as the order is unpaid, regardless of
  // how far later steps have progressed (see getActiveStepIndex), so this reliably
  // gates the payment-reminder action to unpaid orders only.
  const paymentStepActive = activeIndex !== -1 && steps[activeIndex]?.key === "payment";
  // The final step can be completed by hand (like invoice) when an order never
  // reaches the native FULFILLED status; it stays reversible via an "Undo".
  const receivedManuallyMarked = isReceivedManuallyMarked(order);
  const receivedStepActive = activeIndex !== -1 && steps[activeIndex]?.key === "received";

  // "No gaps" rendering: a step only reads as done when it AND every step before
  // it are done. Everything from the first incomplete step onward is treated as
  // not-yet-reached, so the checkmarks always fill left-to-right without holes.
  const firstIncomplete = activeIndex;

  // Resend controls are only meaningful while the order is still in progress; once
  // every step is complete the panel is hidden. Only the natively re-sendable
  // emails are offered: a real invoice (not a manual mark) and a shipping
  // notification (a fulfillment that already carries a tracking number).
  const isStepCompleted = (key: string): boolean => {
    const index = steps.findIndex(step => step.key === key);

    return index !== -1 && firstIncomplete !== -1 && index < firstIncomplete;
  };
  const hasRealInvoice = (order.invoices?.length ?? 0) > 0;
  const canResendInvoice = isStepCompleted("invoice") && hasRealInvoice;
  const canResendShipping = isStepCompleted("shipping");
  // Confirmation and payment emails have no native resend, so they go through the
  // custom backend `orderSendNotification` mutation once their step is complete.
  const canResendConfirmation = isStepCompleted("submitted");
  const canResendPayment = isStepCompleted("payment");
  // Surface every shipment's tracking number next to the progress; when the
  // tracking number is itself a URL (backend `is_tracking_number_url`), link it.
  const trackingNumbers = Array.from(
    new Set(
      (order.fulfillments ?? [])
        .map(fulfillment => fulfillment?.trackingNumber?.trim())
        .filter((trackingNumber): trackingNumber is string => !!trackingNumber),
    ),
  );
  // Shown even once every step is done: the customer still needs the address.
  const pontoRecolhaMissing = isPontoRecolhaAddressMissing(order);
  const showResendSection =
    activeIndex !== -1 &&
    (canResendInvoice || canResendShipping || canResendConfirmation || canResendPayment);

  return (
    <DashboardCard data-test-id="order-progress-stepper">
      <DashboardCard.Content>
        <Box display="flex" overflowX="auto" paddingY={2}>
          {steps.map((step, index) => {
            const isCompleted = firstIncomplete === -1 || index < firstIncomplete;
            const isActive = index === firstIncomplete;
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;

            const circleColor = isCompleted ? "accent1" : "default1";
            const circleBorderColor = isCompleted || isActive ? "accent1" : "default2";
            const labelColor = isCompleted || isActive ? "accent1" : "default2";
            // The connector leading into a step is highlighted once the
            // previous step is completed.
            const connectorBeforeActive = !isFirst && index - 1 < firstIncomplete;
            const connectorBeforeActiveColor =
              firstIncomplete === -1 || connectorBeforeActive ? "accent1" : "default2";
            const connectorAfterActiveColor =
              firstIncomplete === -1 || index < firstIncomplete ? "accent1" : "default2";

            return (
              <Box
                key={step.key}
                display="flex"
                flexDirection="column"
                alignItems="center"
                __flex="1 1 0"
                __minWidth="72px"
              >
                <Box display="flex" alignItems="center" width="100%">
                  <Box
                    __height="2px"
                    __flex="1"
                    backgroundColor={isFirst ? "transparent" : connectorBeforeActiveColor}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor={circleColor}
                    borderColor={circleBorderColor}
                    borderStyle="solid"
                    borderWidth={1}
                    style={{
                      width: CIRCLE_SIZE,
                      height: CIRCLE_SIZE,
                      borderRadius: "50%",
                      flexShrink: 0,
                    }}
                  >
                    <Text
                      size={2}
                      fontWeight="bold"
                      color={isCompleted ? "buttonDefaultPrimary" : labelColor}
                    >
                      {isCompleted ? "✓" : index + 1}
                    </Text>
                  </Box>
                  <Box
                    __height="2px"
                    __flex="1"
                    backgroundColor={isLast ? "transparent" : connectorAfterActiveColor}
                  />
                </Box>
                <Text
                  size={2}
                  fontWeight={isActive ? "bold" : "regular"}
                  color={labelColor}
                  marginTop={2}
                  textAlign="center"
                >
                  <FormattedMessage {...step.label} />
                </Text>
                {step.key === "invoice" && invoiceManuallyMarked && (
                  <Box marginTop={1}>
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={() => handlers.onInvoiceMarkToggle(false)}
                      data-test-id="stepper-undo-invoice"
                    >
                      <FormattedMessage {...orderProgressStepperMessages.undoInvoiceAction} />
                    </Button>
                  </Box>
                )}
                {step.key === "readyForPickup" &&
                  isPickupReadyNotified(order.metadata) &&
                  handlers.onPickupReadyMarkToggle && (
                    <Box marginTop={1}>
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => handlers.onPickupReadyMarkToggle?.(false)}
                        data-test-id="stepper-undo-pickup-ready"
                      >
                        <FormattedMessage {...orderProgressStepperMessages.undoPickupReadyAction} />
                      </Button>
                    </Box>
                  )}
                {step.key === "received" && receivedManuallyMarked && (
                  <Box marginTop={1}>
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={() => handlers.onReceivedMarkToggle(false)}
                      data-test-id="stepper-undo-received"
                    >
                      <FormattedMessage {...orderProgressStepperMessages.undoReceivedAction} />
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {trackingNumbers.length > 0 && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            marginTop={3}
            data-test-id="stepper-tracking"
          >
            {trackingNumbers.map(trackingNumber => (
              <Text key={trackingNumber} size={2} color="default2">
                <FormattedMessage
                  {...orderProgressStepperMessages.trackingLabel}
                  values={{
                    trackingNumber: /^[-\w]+:\/\//.test(trackingNumber) ? (
                      <Text
                        as="a"
                        size={2}
                        color="accent1"
                        href={trackingNumber}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ wordBreak: "break-all" }}
                      >
                        {trackingNumber}
                      </Text>
                    ) : (
                      <Text size={2} fontWeight="medium" color="default1">
                        {trackingNumber}
                      </Text>
                    ),
                  }}
                />
              </Text>
            ))}
          </Box>
        )}

        {primaryAction && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={1}
            marginTop={4}
            paddingTop={4}
            borderTopWidth={1}
            borderTopStyle="solid"
            borderColor="default1"
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Text size={2} color="default2">
                <FormattedMessage {...orderProgressStepperMessages.nextStepLabel} />
              </Text>
              <Button
                variant="primary"
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                data-test-id={primaryAction.testId}
              >
                <FormattedMessage {...primaryAction.label} />
              </Button>
            </Box>
            {primaryAction.disabledReason && (
              <Text color="critical1" size={2} fontWeight="light">
                <FormattedMessage {...primaryAction.disabledReason} />
              </Text>
            )}
            {invoiceStepActive && (
              <Button
                variant="tertiary"
                size="small"
                onClick={() => handlers.onInvoiceMarkToggle(true)}
                data-test-id="stepper-mark-invoice-done"
              >
                <FormattedMessage {...orderProgressStepperMessages.markInvoiceManuallyAction} />
              </Button>
            )}
          </Box>
        )}

        {pontoRecolhaMissing && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={2}
            marginTop={4}
            padding={3}
            borderRadius={3}
            borderWidth={1}
            borderStyle="solid"
            borderColor="critical1"
            data-test-id="stepper-ponto-recolha-missing"
          >
            <Text size={2} color="critical1">
              <FormattedMessage {...orderProgressStepperMessages.pontoRecolhaMissing} />
            </Text>
            <Button
              variant="primary"
              size="small"
              onClick={handlers.onResendShipping}
              data-test-id="stepper-add-ponto-recolha"
            >
              <FormattedMessage {...orderProgressStepperMessages.pontoRecolhaAddAction} />
            </Button>
          </Box>
        )}

        {(receivedStepActive || paymentStepActive || showResendSection) && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={2}
            marginTop={4}
            paddingTop={4}
            borderTopWidth={1}
            borderTopStyle="solid"
            borderColor="default1"
          >
            {receivedStepActive && (
              <Button
                variant="secondary"
                size="small"
                onClick={() => handlers.onReceivedMarkToggle(true)}
                data-test-id="stepper-mark-received"
              >
                <FormattedMessage {...orderProgressStepperMessages.markReceivedAction} />
              </Button>
            )}
            {paymentStepActive && (
              <Button
                variant="secondary"
                size="small"
                onClick={handlers.onSendPaymentReminder}
                data-test-id="stepper-send-payment-reminder"
              >
                <FormattedMessage {...orderProgressStepperMessages.sendPaymentReminderAction} />
              </Button>
            )}
            {showResendSection && (
              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                <Text size={2} color="default2">
                  <FormattedMessage {...orderProgressStepperMessages.resendSectionLabel} />
                </Text>
                <Box display="flex" gap={2} flexWrap="wrap" justifyContent="flex-end">
                  {canResendConfirmation && (
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={handlers.onResendConfirmation}
                      data-test-id="stepper-resend-confirmation"
                    >
                      <FormattedMessage
                        {...orderProgressStepperMessages.resendConfirmationAction}
                      />
                    </Button>
                  )}
                  {canResendPayment && (
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={handlers.onResendPayment}
                      data-test-id="stepper-resend-payment"
                    >
                      <FormattedMessage {...orderProgressStepperMessages.resendPaymentAction} />
                    </Button>
                  )}
                  {canResendInvoice && (
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={handlers.onResendInvoice}
                      data-test-id="stepper-resend-invoice"
                    >
                      <FormattedMessage {...orderProgressStepperMessages.resendInvoiceAction} />
                    </Button>
                  )}
                  {canResendShipping && (
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={handlers.onResendShipping}
                      data-test-id="stepper-resend-shipping"
                    >
                      <FormattedMessage {...orderProgressStepperMessages.resendShippingAction} />
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DashboardCard.Content>
    </DashboardCard>
  );
};

OrderProgressStepper.displayName = "OrderProgressStepper";
